import type { BagType, Contribution, Insurance, LowestFare, LowestFareFilterType } from '@optionway/models';
import type { TagPlanEcommerce, TagPlanSearchContext, FunnelPage } from '#imports';

// Pushed with every event. The container reads these as Data Layer Variables; none is derived on the GTM side.
interface TagPlanPageContext {
  page_type?: string;
  language?: string;
  partner_id?: string;
  display_currency?: string;
}

// Fed by app.vue, the one place that holds the route, the locale and the partner cookie in a component scope.
interface GtmStore {
  pageType?: string;
  language?: string;
  partnerId?: string;
  checkoutBegun: boolean;
  pendingEvents: PendingEvent[];
}

interface PendingEvent {
  event: TagPlanEvent;
  payload: TagPlanPayload;
}

interface PageContextInput {
  routeBaseName?: string | symbol;
  language: string;
  partnerId: string;
}

interface GtmDataLayer {
  push: (entry: Record<string, unknown>) => unknown;
}

// Outside state(): a proxy is not serialisable. app.vue hands it over once, and a push before that is
// dropped rather than registering the script a second time, without the Consent Mode defaults.
let dataLayer: GtmDataLayer | undefined;

// The event catalogue of docs/tag-plan.md § Tagging plan: a name outside it does not compile.
type TagPlanEvent =
  | 'page_view'
  | 'lowest_fare_clicked'
  | 'newsletter_signup'
  | 'search'
  | 'search_submitted'
  | 'search_no_result'
  | 'view_item_list'
  | 'filter_applied'
  | 'sort_changed'
  | 'select_item'
  | 'view_item'
  | 'share'
  | 'begin_checkout'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'add_baggage_info'
  | 'add_passenger_info'
  | 'add_insurance_info'
  | 'funnel_step_error'
  | 'add_payment_info'
  | 'purchase'
  | 'purchase_failed'
  | 'offer_expiration_warning'
  | 'offer_expired'
  | 'contribution_displayed'
  | 'contact_opened'
  | 'locale_changed'
  | 'currency_changed';

// Everything an event may carry on top of the page context. Reference: docs/tag-plan.md.
type TagPlanPayload = TagPlanSearchContext & {
  ecommerce?: TagPlanEcommerce;
  page_location?: string;
  page_title?: string;
  search_term?: string;
  is_prefilled?: boolean;
  search_results_count?: number;
  active_filters_count?: number;
  sort_by?: string;
  filter_type?: string;
  booking_status?: string;
  error_field?: string;
  error_count?: number;
  expiration_regime?: string;
  lowest_fare_filter?: string;
  lowest_fare_price?: number;
  contact_channel?: string;
  method?: string;
  content_type?: string;
  language_from?: string;
  language_to?: string;
  currency_from?: string;
  currency_to?: string;
};

// GTM's data layer model persists every key it has seen: what an event does not write is cleared here.
// Typed on the payload so that a new key cannot be left out.
const PAYLOAD_RESET: Record<keyof TagPlanPayload, undefined> = {
  trip_type: undefined,
  cabin_class: undefined,
  origin: undefined,
  destination: undefined,
  route: undefined,
  departure_date: undefined,
  return_date: undefined,
  booking_window: undefined,
  pax_adults: undefined,
  pax_children: undefined,
  pax_infants: undefined,
  pax_total: undefined,
  legs_count: undefined,
  ecommerce: undefined,
  page_location: undefined,
  page_title: undefined,
  search_term: undefined,
  is_prefilled: undefined,
  search_results_count: undefined,
  active_filters_count: undefined,
  sort_by: undefined,
  filter_type: undefined,
  booking_status: undefined,
  error_field: undefined,
  error_count: undefined,
  expiration_regime: undefined,
  lowest_fare_filter: undefined,
  lowest_fare_price: undefined,
  contact_channel: undefined,
  method: undefined,
  content_type: undefined,
  language_from: undefined,
  language_to: undefined,
  currency_from: undefined,
  currency_to: undefined,
};

// Single writer to the dataLayer: every event leaves from here, through the proxy of the GTM script
// that app.vue loads. Event catalogue and payload contract: docs/tag-plan.md.
export const useGtmStore = defineStore('gtm', {
  state: (): GtmStore => ({
    pageType: undefined,
    language: undefined,
    partnerId: undefined,
    checkoutBegun: false,
    pendingEvents: [],
  }),
  getters: {
    pageContext(state): TagPlanPageContext {
      return {
        page_type: state.pageType,
        language: state.language,
        partner_id: state.partnerId,
        display_currency: useCurrencyStore().currency,
      };
    },
  },
  actions: {
    connect(proxyDataLayer?: GtmDataLayer) {
      dataLayer = proxyDataLayer;
      const pendingEvents = this.pendingEvents;
      this.pendingEvents = [];
      for (const { event, payload } of pendingEvents) {
        this.push(event, payload);
      }
    },

    // The i18n base name, not the path: /results and /fr/results are one page.
    setPageContext({ routeBaseName, language, partnerId }: PageContextInput) {
      this.pageType = tagPlanPageType(routeBaseName);
      this.language = language;
      this.partnerId = partnerId;
    },

    // The ecommerce reset is a push of its own: GTM processes one queue entry per push.
    push(event: TagPlanEvent, payload?: TagPlanPayload) {
      if (!payload) {
        return;
      }
      // GTM only runs in the browser: a server-render push crosses in the payload and leaves on connect.
      if (useRequestEvent()) {
        this.pendingEvents.push({ event, payload });
        return;
      }
      if (!dataLayer) {
        return;
      }
      // eslint-disable-next-line unicorn/no-null
      dataLayer.push({ ecommerce: null });
      // eslint-disable-next-line unicorn/prefer-single-call
      dataLayer.push({ event, ...this.pageContext, ...PAYLOAD_RESET, ...payload });
    },

    pushEcommerce(event: TagPlanEvent, ecommerce?: TagPlanEcommerce) {
      this.push(event, ecommerce ? { ...useTagPlanStore().searchContext, ecommerce } : undefined);
    },

    pushCartChange(previous?: TagPlanEcommerce, next?: TagPlanEcommerce) {
      this.pushEcommerce('remove_from_cart', previous);
      this.pushEcommerce('add_to_cart', next);
    },

    trackPageView(href: string, title: string) {
      this.push('page_view', { page_location: stripEmail(href), page_title: title });
    },

    trackSearch(isPrefilled: boolean) {
      const context = useTagPlanStore().searchContext;
      this.push('search', { ...context, search_term: context.route });
      this.push('search_submitted', { ...context, is_prefilled: isPrefilled });
    },

    trackResults() {
      const tagPlan = useTagPlanStore();
      if (useSearchResultIndexStore().noResult) {
        this.push('search_no_result', tagPlan.searchContext);
        return;
      }
      this.push('view_item_list', {
        ...tagPlan.searchContext,
        ...tagPlan.listContext,
        ecommerce: tagPlan.resultsImpression,
      });
    },

    trackFilterApplied() {
      const tagPlan = useTagPlanStore();
      this.push('filter_applied', {
        ...tagPlan.searchContext,
        filter_type: useSearchResultFiltersStore().activeFilterTypes.join(',') || undefined,
        ...tagPlan.listContext,
      });
    },

    trackSortChanged() {
      this.push('sort_changed', { ...useTagPlanStore().searchContext, sort_by: useSortOptionsStore().sortBy });
    },

    trackSelectItem(offerUuid: string) {
      this.pushEcommerce('select_item', useTagPlanStore().offerSelection(offerUuid));
    },

    trackViewItem() {
      this.pushEcommerce('view_item', useTagPlanStore().pricedOffer);
    },

    trackShare() {
      this.push('share', { ...useTagPlanStore().searchContext, method: 'copy_link', content_type: 'search' });
    },

    // One entry per pricing: the step is mounted again on every step back, which is not a new funnel entry.
    trackBeginCheckout() {
      if (this.checkoutBegun) {
        return;
      }
      this.checkoutBegun = true;
      this.pushEcommerce('begin_checkout', useTagPlanStore().pricedOffer);
    },

    // The gesture measured its own effect, so nothing here remembers a cart. A count that did not
    // move while the price did is a swap, and GA4 reads a swap as the old line out, the new one in.
    trackBagChange(bagType: BagType, countBefore: number, valueBefore: number, countAfter: number, valueAfter: number) {
      if (countAfter === countBefore) {
        if (roundToCents(valueAfter - valueBefore) === 0) {
          return;
        }
        this.pushEcommerce('remove_from_cart', bagCartEcommerce(bagType, countBefore, valueBefore));
        this.pushEcommerce('add_to_cart', bagCartEcommerce(bagType, countAfter, valueAfter));
        return;
      }
      const added = countAfter > countBefore;
      this.pushEcommerce(
        added ? 'add_to_cart' : 'remove_from_cart',
        bagCartEcommerce(bagType, Math.abs(countAfter - countBefore), Math.abs(roundToCents(valueAfter - valueBefore))),
      );
    },

    // Called before the repricing empties the cart, so the live one is still the one to report.
    trackBagCartCleared() {
      const luggages = useLuggagesStore();
      for (const bagType of luggages.selectedBagTypes) {
        this.pushEcommerce(
          'remove_from_cart',
          bagCartEcommerce(
            bagType,
            luggages.selectedCount(bagType),
            roundToCents(luggages.selectedBagsPriceByType(bagType)),
          ),
        );
      }
    },

    trackInsuranceChange(previous?: Insurance, next?: Insurance) {
      if (previous?.id !== next?.id) {
        this.pushCartChange(insuranceEcommerce(previous), insuranceEcommerce(next));
      }
    },

    trackContributionChange(previous?: Contribution, next?: Contribution) {
      if (previous?.ratio !== next?.ratio) {
        this.pushCartChange(contributionEcommerce(previous), contributionEcommerce(next));
      }
    },

    // A repricing empties the cart from the results page, where no watcher is mounted to notice,
    // and opens a new funnel entry.
    trackCartCleared() {
      this.checkoutBegun = false;
      this.trackBagCartCleared();
      this.trackInsuranceChange(useSelectedInsuranceStore().insurance);
      this.trackContributionChange(useSelectedContributionStore().contribution);
    },

    // The offer is conditional and GA4 cannot tell: without this impression the attach rate has no denominator.
    trackContributionDisplayed() {
      this.push('contribution_displayed', useTagPlanStore().searchContext);
    },

    trackStepCompleted(page: FunnelPage) {
      const event = funnelCompletionEvent(page);
      if (!event) {
        return;
      }
      const tagPlan = useTagPlanStore();
      const ecommerce = tagPlan.stepEcommerce(page);
      this.push(event, ecommerce ? { ...tagPlan.searchContext, ecommerce } : tagPlan.searchContext);
    },

    // Counts only — never which field, never what was typed.
    trackFunnelStepError(error: { error_count: number; error_field?: string }) {
      this.push('funnel_step_error', { ...useTagPlanStore().searchContext, ...error });
    },

    // The billed cart is stashed right after: the payment return is a document POST, so the cart is
    // gone by the time /pending needs it for the purchase event.
    trackPaymentSubmitted() {
      const tagPlan = useTagPlanStore();
      const ecommerce = tagPlan.checkoutEcommerce;
      this.push('add_payment_info', { ...tagPlan.searchContext, ecommerce });
      // sessionStorage, not a cookie: it never reaches the server and dies with the tab. No personal data.
      try {
        globalThis.sessionStorage?.setItem(HANDOVER_KEY, JSON.stringify(ecommerce));
      } catch {
        /* a full or unavailable sessionStorage must never break the payment flow */
      }
    },

    trackPurchase() {
      const recordId = useBookingStatusStore().recordId;
      if (!recordId) {
        return;
      }
      let handover: string | undefined;
      try {
        handover = globalThis.sessionStorage?.getItem(HANDOVER_KEY) ?? undefined;
        globalThis.sessionStorage?.removeItem(HANDOVER_KEY);
      } catch {
        /* an unavailable sessionStorage costs the revenue, never the conversion */
      }
      this.pushEcommerce('purchase', purchaseEcommerce(recordId, parseCheckoutHandover(handover)));
    },

    trackPurchaseFailed(status: string) {
      this.push('purchase_failed', { ...useTagPlanStore().searchContext, booking_status: status });
    },

    trackOfferExpiration(phase: 'warning' | 'expired') {
      this.push(phase === 'warning' ? 'offer_expiration_warning' : 'offer_expired', {
        ...useTagPlanStore().searchContext,
        expiration_regime: useOfferExpirationStore().regime,
      });
    },

    // The card values, not a committed search: the visitor has not searched anything yet.
    trackLowestFareClicked(fare: LowestFare, filter?: LowestFareFilterType) {
      this.push('lowest_fare_clicked', {
        origin: useSearchCriteriaStore().originCodeWithDefault,
        destination: fare.cityCode,
        departure_date: fare.departureDate,
        return_date: fare.returnDate,
        lowest_fare_price: fare.price,
        lowest_fare_filter: filter,
      });
    },

    trackNewsletterSignup() {
      this.push('newsletter_signup', {});
    },

    trackContactOpened() {
      this.push('contact_opened', { contact_channel: 'beacon' });
    },

    trackLocaleChanged(from: string, to: string) {
      this.push('locale_changed', { language_from: from, language_to: to });
    },

    trackCurrencyChanged(from: string, to: string) {
      this.push('currency_changed', { currency_from: from, currency_to: to });
    },
  },
});

const HANDOVER_KEY = 'ow-tag-plan-checkout';
const PARAM_EMAIL = 'email';

// Record<FunnelPage, …>: a step added to funnelPages fails the typecheck until it is mapped here.
// billing and payment complete elsewhere (add_payment_info, purchase) — docs/tag-plan.md § Checkout.
const STEP_COMPLETION_EVENT: Record<FunnelPage, TagPlanEvent | undefined> = {
  luggages: 'add_baggage_info',
  passengers: 'add_passenger_info',
  insurances: 'add_insurance_info',
  billing: undefined,
  payment: undefined,
};

// `index` reads as `home` in a report; a symbol is a route nobody named.
function tagPlanPageType(routeBaseName?: string | symbol): string | undefined {
  if (typeof routeBaseName !== 'string') {
    return;
  }
  return routeBaseName === 'index' ? 'home' : routeBaseName;
}

function funnelCompletionEvent(page?: FunnelPage): TagPlanEvent | undefined {
  return page ? STEP_COMPLETION_EVENT[page] : undefined;
}

function bagCartEcommerce(bagType: BagType, count: number, value: number): TagPlanEcommerce {
  return {
    currency: TAG_PLAN_CURRENCY,
    value,
    items: [bagTagPlanItem(bagType, count, value)],
  };
}

function contributionEcommerce(contribution?: Contribution): TagPlanEcommerce | undefined {
  if (!contribution) {
    return;
  }
  return { currency: TAG_PLAN_CURRENCY, value: contribution.price, items: [contributionTagPlanItem(contribution)] };
}

// transaction_id is the recordId — a payment attempt, which GA4 deduplicates on. The cart cannot be read
// on /pending (the payment return is a document POST), so the monetary part arrives via the handover.
function purchaseEcommerce(recordId: number, handover?: TagPlanEcommerce): TagPlanEcommerce {
  return {
    ...handover,
    items: handover?.items ?? [],
    transaction_id: String(recordId),
    currency: TAG_PLAN_CURRENCY,
  };
}

function isEcommerce(value: unknown): value is TagPlanEcommerce {
  if (typeof value !== 'object' || value === null || !('items' in value)) {
    return false;
  }
  return Array.isArray(value.items);
}

function parseCheckoutHandover(raw?: string): TagPlanEcommerce | undefined {
  if (!raw) {
    return;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isEcommerce(parsed) ? parsed : undefined;
  } catch {
    return;
  }
}

// The payment return URL carries the passenger's e-mail until pages/pending.vue strips it, and the page_view
// leaves first: the never-send parameter is dropped here, whatever the page does with its URL.
function stripEmail(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(PARAM_EMAIL);
  return url.href;
}
